/**
 * Logout Service
 *
 * This module provides functions for handling user logout operations,
 * including clearing authentication data and redirecting to login page.
 */

import { clearAllAuthData } from "./cookieAuthService";
import { getAuthPaths, getFullPath } from "@/config/basePath";
import { LOGOUT_ENDPOINT_INSIDE } from "@/config/endpoints/insideEndpoints";

/**
 * Performs complete logout operation.
 * Calls the server-side logout API to delete the Redis session,
 * clears localStorage, and redirects to login page.
 */
export const performLogout = async (): Promise<void> => {
  try {
    // POST to clear Redis session and cookies server-side
    const { fetchWithXSRF } = await import("@/lib/fetchWithXSRF");
    await fetchWithXSRF(LOGOUT_ENDPOINT_INSIDE, { method: "POST" });
    // Redirect client-side so the URL uses the browser's actual origin
    // (avoids Docker's internal 0.0.0.0 address leaking into redirects)
    window.location.href = getFullPath("/auth/login");
  } catch (error) {
    console.error("Error during logout:", error);
    // Fallback: clear client-side data and redirect
    await clearAllAuthData();
    const { LOGIN_URL } = getAuthPaths();
    const fallbackBase =
      typeof window !== "undefined" ? window.location.origin : "";
    window.location.href = `${fallbackBase}${LOGIN_URL}`;
  }
};

/**
 * Performs logout due to idle timeout
 * Similar to regular logout but can include additional logging
 */
export const performIdleLogout = async (): Promise<void> => {
  try {
    console.log("User logged out due to idle timeout");
    await performLogout();
  } catch (error) {
    console.error("Error during idle logout:", error);
    const { LOGIN_URL } = getAuthPaths();
    const fallbackBase =
      typeof window !== "undefined" ? window.location.origin : "";
    window.location.href = `${fallbackBase}${LOGIN_URL}`;
  }
};
